import type { Camera, Size } from './camera';
import { overlaps, pad, placeLabels, type Rect } from './labels';
import { NO_INSET, overviewHudKeepOut, panelBoxW, type HudInset } from './mapHud';
import { STRIP_GLINTS, STRIP_PLACES } from './overviewData';
import { PANEL_LINE, STRIP_LAMPS, STRIP_WORLDS, type Pt } from './overview';
import { stopCamera, toScreen, type StripLayout } from './overviewCamera';
import { REALM_SPOTS, SECRET_SPOTS } from './spots';
import { boxMeetsPolygon } from './worlds';

/**
 * The side places' pins and the secrets' glints on the strip: how much screen they take, what they must keep clear of, and which
 * stop shows each one best. Pure (no Pixi), so the positions in overviewData are proven against the art, the controls and the panel
 * at every stop and screen size without a browser. Coordinates are master pixels unless a name says screen.
 *
 * Pins and glints are drawn at screen size whatever the zoom (their scale is 1 / zoom), so their room on the painting grows as the
 * strip zooms out; every test here works in screen pixels at a camera.
 */

/** A pin: its ring (the place's marker, centred this far above its spot, this wide across) and, under the ring, its name plate. The
 *  ring stays on the spot; only the plate may move (RealmPin). Screen pixels. */
export const RING_UP = 44;
export const RING_R = 23;
/** The plate's top is this far above the spot, and it is this high. */
export const PLATE_TOP = 22;
export const PLATE_H = 20;
/** The most the plate moves from where the spot puts it, to stay on screen and clear of the controls, the panel and the other
 *  plates (the ring never moves). */
export const PIN_SHIFT = 64;
/** A place that no stop can show on a small or odd screen (its ring is under the controls or off the edge at every stop: the Troll
 *  Bridge at 320x568, the Lighthouse on a landscape phone) may have its ring slid at most this far from its spot, screen pixels, clear
 *  of them, with a stem back to the spot (RealmPin). Every other place's ring stays on its spot. */
export const RING_SLIDE = 80;
/** The ring keeps this far from the screen's edge (it is the place's marker, so it is hidden rather than carried away), the plate
 *  this far. */
export const RING_EDGE = 6;
export const PLATE_EDGE = 8;
/** A glint's tap area is a circle this wide (RealmGlint), in screen pixels. */
export const GLINT_R = 24;
/** A pin's plate text: about this wide a letter at 12 px in Lilita One, padded by 14; a waiting place (the Warm Pool's timer) adds
 *  the clock and the time. Measured on 1 Nov 2026 (the browser spec asserts the real plate is never wider than this). */
const PLATE_PER_LETTER = 7.4;
const PLATE_PAD = 14;
const PLATE_WAITING = 64;

/** The word on a place's pin. */
export function pinName(id: string): string {
  return REALM_SPOTS.find((s) => s.id === id)?.name ?? id;
}

/** A pin's plate width, the most it takes (with the timer when `waiting`). */
export function plateWidthEstimate(id: string, waiting = id === 'springs'): number {
  return pinName(id).length * PLATE_PER_LETTER + PLATE_PAD + (waiting ? PLATE_WAITING : 0);
}

/** A pin's whole box at rest (ring and plate), the most it takes. */
export function pinSize(id: string, waiting = id === 'springs'): { w: number; h: number } {
  return { w: Math.max(RING_R * 2 + 6, plateWidthEstimate(id, waiting)), h: RING_UP + RING_R };
}

/** A pin's ring as a box on screen when its spot is at `spot`. */
export function ringRectAt(spot: { x: number; y: number }): Rect {
  return { x: spot.x - RING_R, y: spot.y - RING_UP - RING_R, w: RING_R * 2, h: RING_R * 2 };
}

/** A pin's plate as a box on screen, `w` wide, moved by `off` from where the spot puts it. */
export function plateRectAt(spot: { x: number; y: number }, w: number, off = { x: 0, y: 0 }): Rect {
  return { x: spot.x - w / 2 + off.x, y: spot.y - PLATE_TOP + off.y, w, h: PLATE_H };
}

/** A glint stays screen size, except on a layout so small (a landscape phone: the whole strip is a hundred pixels high) that a full
 *  glint would be wider than the land between a scene's lamps and the names under it: below this layout zoom it shrinks with it. */
export const GLINT_FULL_ZOOM = 0.3;
export function glintScale(layoutZoom: number): number {
  // A little faster than the layout itself, so the glint is clear of its neighbours with a few pixels to spare, not a hair's breadth.
  return Math.min(1, (layoutZoom / GLINT_FULL_ZOOM) ** 1.35);
}

/** A glint's tap area as a box on screen (`k` is {@link glintScale}). */
export function glintRectAt(spot: { x: number; y: number }, k = 1): Rect {
  return { x: spot.x - GLINT_R * k, y: spot.y - GLINT_R * k, w: GLINT_R * 2 * k, h: GLINT_R * 2 * k };
}

/** A place's or a secret's spot on the strip, master pixels. */
export function spotAt(id: string): Pt | null {
  return STRIP_PLACES[id] ?? STRIP_GLINTS[id] ?? null;
}

export const PLACE_IDS: readonly string[] = REALM_SPOTS.map((s) => s.id);
export const GLINT_IDS: readonly string[] = SECRET_SPOTS.map((s) => s.id);

/** A lamp's tap area (StripLamps: 120 wide, from 237 above its foot to 30 below), in master pixels. */
const LAMP_BOX = { left: -60, top: -237, right: 60, bottom: 30 } as const;

/** The lamps' tap areas on screen at a camera. */
export function lampRects(cam: Camera): Rect[] {
  return STRIP_LAMPS.map((l) => {
    const a = toScreen(cam, [l.foot[0] + LAMP_BOX.left, l.foot[1] + LAMP_BOX.top]);
    return { x: a.x, y: a.y, w: (LAMP_BOX.right - LAMP_BOX.left) * cam.zoom, h: (LAMP_BOX.bottom - LAMP_BOX.top) * cam.zoom };
  });
}

/** The scenes' tap shapes on screen at a camera. */
export function sceneShapes(cam: Camera): Pt[][] {
  return STRIP_WORLDS.map((w) => w.tap.map((p): Pt => [cam.x + p[0] * cam.zoom, cam.y + p[1] * cam.zoom]));
}

/** The painted art a pin or glint must stay off at one camera: the lamps' tap areas and the scenes' tap shapes, on screen. Made once
 *  per camera and asked many times. */
export interface Art {
  lamps: Rect[];
  /** The lamps' glass and caps, and their bases: a name plate keeps off these (a lamp's tap goes to whatever is over it), though a ring
   *  may sit on a lamp's post. */
  heads: Rect[];
  shapes: { poly: Pt[]; box: Rect }[];
}

/** A lamp's glass and cap (the top of its sprite and the glow round it) and its base, master pixels about its foot. */
export const LAMP_HEAD = { left: -40, right: 40, top: -232, bottom: -142 } as const;
export const LAMP_BASE = { left: -32, right: 32, top: -6, bottom: 30 } as const;

/** The lamps' heads and bases on screen at a camera. */
export function lampParts(cam: Camera): Rect[] {
  return STRIP_LAMPS.flatMap((l) =>
    [LAMP_HEAD, LAMP_BASE].map((b) => {
      const a = toScreen(cam, [l.foot[0] + b.left, l.foot[1] + b.top]);
      return { x: a.x, y: a.y, w: (b.right - b.left) * cam.zoom, h: (b.bottom - b.top) * cam.zoom };
    }),
  );
}

export function artAt(cam: Camera): Art {
  return {
    lamps: lampRects(cam),
    heads: lampParts(cam),
    shapes: sceneShapes(cam).map((poly) => {
      const xs = poly.map((p) => p[0]);
      const ys = poly.map((p) => p[1]);
      const x = Math.min(...xs);
      const y = Math.min(...ys);
      return { poly, box: { x, y, w: Math.max(...xs) - x, h: Math.max(...ys) - y } };
    }),
  };
}

/** What painted art a box on screen meets: a lamp's tap area (its index), or a scene's tap shape (the world's strip index). */
export function artMeets(art: Art, r: Rect): { lamp: number | null; scene: number | null } {
  const lamp = art.lamps.findIndex((l) => overlaps(l, r));
  const scene = art.shapes.findIndex((s) => overlaps(s.box, r) && boxMeetsPolygon(r, s.poly));
  return { lamp: lamp >= 0 ? lamp : null, scene: scene >= 0 ? scene : null };
}

/** The box is clear of every lamp and scene. */
export function artClear(art: Art, r: Rect): boolean {
  const m = artMeets(art, r);
  return m.lamp === null && m.scene === null;
}

/** The box is wholly on screen, `edge` from its sides. */
export function onScreen(r: Rect, view: Size, edge: number): boolean {
  return r.x >= edge && r.y >= edge && r.x + r.w <= view.w - edge && r.y + r.h <= view.h - edge;
}

/** How far a glint keeps from the screen's edge. */
export const GLINT_EDGE = 8;

/**
 * Whether a glint at `spot` on screen is shown: wholly on screen, and clear of the controls (`keepOut`), the names, the panel and the
 * pins on screen (`names`), the lamps and the scenes. A glint that is not shown cannot be tapped, so it never sits under anything or
 * half under it.
 */
export function glintShown(art: Art, view: Size, spot: { x: number; y: number }, keepOut: readonly Rect[], names: readonly Rect[], k = 1): boolean {
  const r = glintRectAt(spot, k);
  if (!onScreen(r, view, GLINT_EDGE)) return false;
  if (keepOut.some((k) => overlaps(k, r)) || names.some((n) => overlaps(n, r))) return false;
  return artClear(art, r);
}

/** A spot on screen at a camera. */
export function spotScreen(cam: Camera, id: string): { x: number; y: number } | null {
  const p = spotAt(id);
  return p ? toScreen(cam, p) : null;
}

// ---- placing them at a stop ----

/** One pin to place: its spot on screen and how wide its plate is. */
export interface PinSpec {
  id: string;
  spot: { x: number; y: number };
  plateW: number;
  /** The place may slide its ring off its spot ({@link RING_SLIDE}): no stop shows it otherwise. */
  slide?: boolean;
}

/** Where one pin stands at a stop. */
export interface PinPlace {
  /** The ring (on the spot) is on screen and under nothing, and the plate found room: the pin is shown. Otherwise it is hidden at
   *  this stop (a fade, no tap). */
  shown: boolean;
  ring: Rect;
  plate: Rect;
  /** How far the plate moved from where the spot puts it (with the ring, when that slid). */
  offset: { x: number; y: number };
  /** How far the plate moved from under its ring. */
  moved: number;
  /** How far the ring slid from its spot: none, but for a place no stop shows otherwise ({@link RING_SLIDE}). */
  ringOffset: { x: number; y: number };
  /** Why it is hidden. */
  why: '' | 'ring off screen' | 'ring under a control' | 'ring under the panel or a name' | 'ring on another ring' | 'no room for the plate';
}

export interface StopLayout {
  pins: Record<string, PinPlace>;
  glints: Record<string, { at: { x: number; y: number }; shown: boolean }>;
}

const OFFSET_STEP = 4;

/** The smallest move of a plate from where its spot puts it (at most {@link PIN_SHIFT}) that puts it wholly on screen, {@link PLATE_EDGE}
 *  in, and clear of `blockers`; null when there is none. */
export function findPlateOffset(
  spot: { x: number; y: number },
  plateW: number,
  view: Size,
  blockers: readonly Rect[],
): { x: number; y: number } | null {
  let best: { x: number; y: number } | null = null;
  let bestD = Infinity;
  const reach = Math.ceil(PIN_SHIFT / OFFSET_STEP) * OFFSET_STEP;
  for (let dx = -reach; dx <= reach; dx += OFFSET_STEP) {
    for (let dy = -reach; dy <= reach; dy += OFFSET_STEP) {
      const d = Math.hypot(dx, dy);
      if (d > PIN_SHIFT || d >= bestD) continue;
      const r = plateRectAt(spot, plateW, { x: dx, y: dy });
      if (!onScreen(r, view, PLATE_EDGE) || blockers.some((k) => overlaps(k, r))) continue;
      best = { x: dx, y: dy };
      bestD = d;
    }
  }
  return best;
}

/** The smallest slide of a ring from its spot (at most {@link RING_SLIDE}) that puts it wholly on screen, {@link RING_EDGE} in, and clear
 *  of `blockers`; null when there is none, or when the spot itself is off screen (the stem would point at nothing). */
export function findRingOffset(spot: { x: number; y: number }, view: Size, blockers: readonly Rect[]): { x: number; y: number } | null {
  if (spot.x < 0 || spot.y < 0 || spot.x > view.w || spot.y > view.h) return null;
  let best: { x: number; y: number } | null = null;
  let bestD = Infinity;
  for (let dy = -RING_SLIDE; dy <= RING_SLIDE; dy += OFFSET_STEP) {
    for (let dx = -RING_SLIDE; dx <= RING_SLIDE; dx += OFFSET_STEP) {
      const d = Math.hypot(dx, dy);
      if (d > RING_SLIDE || d >= bestD) continue;
      const r = ringRectAt({ x: spot.x + dx, y: spot.y + dy });
      if (!onScreen(r, view, RING_EDGE) || blockers.some((k) => overlaps(k, r))) continue;
      best = { x: dx, y: dy };
      bestD = d;
    }
  }
  return best;
}

/**
 * Where the pins and glints stand at a stop (the camera at rest on it), once, for the stop (the scene holds the result through a pan
 * and fades changes in and out). `names` are the panel and the other worlds' names as shown at the stop.
 *
 * A pin's ring stays on its spot: it may overlap a scene's or a lamp's edge (a pin is drawn on top and wins the tap), but it is hidden
 * at the stop when the ring is not wholly on screen or is under a control, the panel, a name or another ring (a pin marked `slide`
 * instead slides its ring clear of the controls and the edge, at most {@link RING_SLIDE}, where it can). Only its plate moves,
 * by at most {@link PIN_SHIFT}, to stay on screen and clear of the controls, the panel and names, the glints, the rings and the other
 * plates; a pin whose plate has no such room is hidden too. A glint is shown only where nothing is over it, a pin included.
 */
export function layoutPins(
  view: Size,
  keep: readonly Rect[],
  names: readonly Rect[],
  art: Art,
  pins: readonly PinSpec[],
  glintSpots: readonly { id: string; spot: { x: number; y: number } }[],
  glintK = 1,
): StopLayout {
  const out: StopLayout = { pins: {}, glints: {} };
  const rings = pins.map((p) => ringRectAt(p.spot));
  const live: boolean[] = pins.map((p, i) => {
    let r = rings[i]!;
    const earlier = (j: number): boolean => j < i && out.pins[pins[j]!.id]?.why === '';
    let why: PinPlace['why'] = '';
    if (!onScreen(r, view, RING_EDGE)) why = 'ring off screen';
    else if (keep.some((k) => overlaps(k, r))) why = 'ring under a control';
    else if (names.some((n) => overlaps(n, r))) why = 'ring under the panel or a name';
    else if (pins.some((_, j) => earlier(j) && overlaps(rings[j]!, r))) why = 'ring on another ring';
    let ringOffset = { x: 0, y: 0 };
    if (p.slide && (why === 'ring off screen' || why === 'ring under a control')) {
      const off = findRingOffset(p.spot, view, [...keep, ...names, ...rings.filter((_, j) => earlier(j))]);
      if (off) {
        ringOffset = off;
        r = ringRectAt({ x: p.spot.x + off.x, y: p.spot.y + off.y });
        rings[i] = r;
        why = '';
      }
    }
    out.pins[p.id] = { shown: why === '', ring: r, plate: plateRectAt(p.spot, p.plateW, ringOffset), offset: ringOffset, moved: 0, ringOffset, why };
    return why === '';
  });
  const liveRings = rings.filter((_, i) => live[i]);
  // Glints first against what cannot move (the rings), so the plates can keep off the ones that will show.
  const first = glintSpots.filter((g) => glintShown(art, view, g.spot, keep, [...names, ...liveRings], glintK));
  const glintBoxes = first.map((g) => glintRectAt(g.spot, glintK));
  const taken: Rect[] = [];
  // The widest plates first: they have the least room, and the narrow ones can go round them.
  const widest = pins.map((_, i) => i).sort((a, b) => pins[b]!.plateW - pins[a]!.plateW);
  widest.forEach((i) => {
    const p = pins[i]!;
    if (!live[i]) return;
    const place = out.pins[p.id]!;
    // The plate's home is under its ring, wherever the ring stands.
    const ro = place.ringOffset;
    const home = { x: p.spot.x + ro.x, y: p.spot.y + ro.y };
    // A plate keeps off its own ring (a sliver where they meet is fine) and off the lamps' glass and bases, and off other rings, glints,
    // names and controls. Where there is no room for that, it gives up the lamps first, then its own ring (a tiny layout).
    const own = rings[i]!;
    const ownCore = { x: own.x + 5, y: own.y + 5, w: own.w - 10, h: own.h - 10 };
    const blockers = [...keep, ...names, ...glintBoxes, ...liveRings.filter((r) => r !== rings[i]), ...taken];
    const best =
      findPlateOffset(home, p.plateW, view, [...blockers, ownCore, ...art.heads]) ??
      findPlateOffset(home, p.plateW, view, [...blockers, ownCore]) ??
      findPlateOffset(home, p.plateW, view, [...blockers, ...art.heads]) ??
      findPlateOffset(home, p.plateW, view, blockers);
    if (!best) {
      place.shown = false;
      place.why = 'no room for the plate';
      live[i] = false;
      return;
    }
    place.offset = { x: ro.x + best.x, y: ro.y + best.y };
    place.moved = Math.hypot(best.x, best.y);
    place.plate = plateRectAt(p.spot, p.plateW, place.offset);
    taken.push(pad(place.plate, 2));
  });
  // Then every glint against the pins that stay.
  const stay: Rect[] = pins.flatMap((p, i) => (live[i] ? [out.pins[p.id]!.ring, out.pins[p.id]!.plate] : []));
  for (const g of glintSpots) out.glints[g.id] = { at: g.spot, shown: glintShown(art, view, g.spot, keep, [...names, ...stay], glintK) };
  return out;
}

const NAME_H = 26;

/** The panel and the other worlds' names at a stop, as the painted map places them (clear of the controls and each other), from
 *  estimated sizes: the pure stand-in for what the scene measures. Returns the shown names' boxes (the panel first) and the panel's. */
export function stopNames(lay: StripLayout, view: Size, inset: HudInset, stop: number): { names: Rect[]; panel: Rect; panelShown: boolean } {
  const cam = stopCamera(lay, view, stop);
  const keep = overviewHudKeepOut(view, inset);
  const panelW = panelBoxW(view, inset);
  // The panel's box as the scene measures it (plate, gap and Visit button, and the button's shadow): 127 high, 105 on a short screen.
  const panelH = lay.anchors.compact ? 105 : 127;
  const items = STRIP_WORLDS.map((w, i) => {
    const spot = { x: cam.x + w.stop * cam.zoom, y: cam.y + (i === stop ? PANEL_LINE : PANEL_LINE + 70) * cam.zoom };
    const width = i === stop ? panelW : Math.ceil(w.name.length * 9.9);
    const h = i === stop ? panelH : NAME_H;
    // The panel hangs from its spot (its origin is the plate's top middle); a name is centred on its spot (anchor .5).
    return { spot, want: { x: spot.x - width / 2, y: i === stop ? spot.y : spot.y - h / 2, w: width, h }, maxShift: i === stop ? 150 : 160, slide: true, group: 0 };
  });
  const placed = items.length > 0 ? placeLabels(items, view, keep, 16) : [];
  return {
    names: placed.flatMap((p) => (p.shown ? [p.at] : [])),
    panel: placed[stop]!.at,
    panelShown: placed[stop]!.shown,
  };
}

/** What the scene puts on screen at a stop, from the same rules with estimated sizes, the places in `slide` sliding their rings. */
function placementAt(
  lay: StripLayout,
  view: Size,
  inset: HudInset,
  stop: number,
  slide: ReadonlySet<string>,
): StopLayout & { panel: Rect; panelShown: boolean } {
  const cam = stopCamera(lay, view, stop);
  const keep = overviewHudKeepOut(view, inset);
  const { names, panel, panelShown } = stopNames(lay, view, inset, stop);
  const layout = layoutPins(
    view,
    keep,
    names,
    artAt(cam),
    PLACE_IDS.map((id) => ({ id, spot: spotScreen(cam, id)!, plateW: plateWidthEstimate(id), slide: slide.has(id) })),
    GLINT_IDS.map((id) => ({ id, spot: spotScreen(cam, id)! })),
    glintScale(lay.zoom),
  );
  return { ...layout, panel, panelShown };
}

const NO_SLIDE: ReadonlySet<string> = new Set();
const slideMemo = new Map<string, ReadonlySet<string>>();

/** The places that no stop shows at this screen with every ring on its spot: they may slide theirs ({@link RING_SLIDE}). */
export function slidePlaces(lay: StripLayout, view: Size, inset: HudInset): ReadonlySet<string> {
  const key = `${view.w}x${view.h} ${inset.top},${inset.right},${inset.bottom},${inset.left} ${lay.zoom}`;
  const known = slideMemo.get(key);
  if (known) return known;
  const at = STRIP_WORLDS.map((_, i) => placementAt(lay, view, inset, i, NO_SLIDE));
  const out: ReadonlySet<string> = new Set(PLACE_IDS.filter((id) => !at.some((p) => p.pins[id]!.shown)));
  if (slideMemo.size > 64) slideMemo.clear();
  slideMemo.set(key, out);
  return out;
}

/** What the scene puts on screen at a stop, from the same rules with estimated sizes. */
export function stopPlacement(lay: StripLayout, view: Size, inset: HudInset, stop: number): StopLayout & { panel: Rect; panelShown: boolean } {
  return placementAt(lay, view, inset, stop, slidePlaces(lay, view, inset));
}

/** The inset-free default (a screen with no notch). */
export const stopPlacementPlain = (lay: StripLayout, view: Size, stop: number): ReturnType<typeof stopPlacement> =>
  stopPlacement(lay, view, NO_INSET, stop);

/**
 * The stops to bring a place or a secret into view from, best first (the strip's `focusOn`, for tests and dev): the strip's camera rests
 * only on the worlds' stops (one axis, no zoom, the panel hangs under the chosen scene), so focusing chooses a stop, never an
 * off-stop camera. First the stops that show it by the rules above (estimated sizes), the one with its spot nearest the middle of the
 * screen first; then the others, the world nearest along the road first. The scene tries them in order against its real sizes.
 */
export function focusOrder(lay: StripLayout, view: Size, inset: HudInset, id: string): number[] {
  const isPin = PLACE_IDS.includes(id);
  const p = spotAt(id);
  if (!p) return [0];
  const off = (i: number): number => Math.abs(toScreen(stopCamera(lay, view, i), p).x - view.w / 2) / (view.w / 2);
  const stops = STRIP_WORLDS.map((_, i) => i);
  const seen = stops.filter((i) => {
    const place = stopPlacement(lay, view, inset, i);
    return isPin ? place.pins[id]!.shown : place.glints[id]!.shown;
  });
  seen.sort((a, b) => off(a) - off(b));
  const rest = stops.filter((i) => !seen.includes(i)).sort((a, b) => Math.abs(STRIP_WORLDS[a]!.stop - p[0]) - Math.abs(STRIP_WORLDS[b]!.stop - p[0]));
  return [...seen, ...rest];
}

/** The best stop to focus a place or a secret from: the first of {@link focusOrder} (the world's own stop when nothing shows it). */
export function focusFor(lay: StripLayout, view: Size, inset: HudInset, id: string): number {
  return focusOrder(lay, view, inset, id)[0]!;
}
